import React, { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { OwnershipTypeSelector } from './OwnershipTypeSelector';
import { autoCalculateFields } from './investmentFormUtils';
import { getInitialFormData } from './investments/investmentFormData';
import TypeSpecificFields from './investments/TypeSpecificFields';
import InvestmentFormMiddle from './investments/InvestmentFormMiddle';
import InvestmentBasicFields from './investments/InvestmentBasicFields';
import { useInvestmentFormConfig } from './investments/useInvestmentFormConfig';
import { useInvestmentSubmit } from './hooks/useInvestmentSubmit';
import { useInvestmentValidation } from './hooks/useInvestmentValidation';
import FieldError from './ui/FieldError';
import InlineConfirmCloseBar from './ui/InlineConfirmCloseBar';
import DividendsSection from './investments/DividendsSection';
import SalesSection from './investments/SalesSection';
import IncomeSummaryCard from './investments/IncomeSummaryCard';
import useDirtyConfirm from '../hooks/useDirtyConfirm';
import { toast } from 'sonner';

const AddInvestmentModal = ({ isOpen, onClose, onSuccess, investment = null, user }) => {
  const initialData = useMemo(() => getInitialFormData(investment), [investment]);
  const [formData, setFormData] = useState(initialData);
  const { config, setConfig, entities, relatedAssets } = useInvestmentFormConfig(isOpen);
  const { isLoading, isEditing, handleSubmit, getSubmitLabel } = useInvestmentSubmit(investment, onSuccess);
  const { errors, touch, touchAll, hasErrors } = useInvestmentValidation(formData);

  // Discard guard — "dirty" if any field has drifted from its initial value.
  const isDirty = useMemo(() => {
    try { return JSON.stringify(formData) !== JSON.stringify(initialData); }
    catch { return false; }
  }, [formData, initialData]);
  const { showConfirm, requestClose, confirmDiscard, cancelDiscard } = useDirtyConfirm({ onClose, isDirty });

  const handleInputChange = (field, value) => {
    setFormData(prev => autoCalculateFields(prev, field, value));
    // Mark this field (and any auto-computed siblings, conservatively) as
    // touched so errors become visible. Merely tabbing past an empty field
    // is no longer enough — the user must actually edit it.
    touch(field);
  };

  const onFormSubmit = (e) => {
    e.preventDefault();
    touchAll();
    if (hasErrors) {
      toast.error('Please fix the highlighted fields before saving');
      return;
    }
    handleSubmit(e, formData);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) requestClose(); }}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        onEscapeKeyDown={(e) => { e.preventDefault(); requestClose(); }}
        onInteractOutside={(e) => { e.preventDefault(); requestClose(); }}
      >
        <DialogHeader>
          <DialogTitle className="text-2xl font-serif font-bold text-slate-900">
            {isEditing ? 'Edit Investment' : 'Add New Investment'}
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500 mt-1">Fields marked <span className="text-red-500 font-semibold">*</span> are required</DialogDescription>
        </DialogHeader>

        {showConfirm && (
          <InlineConfirmCloseBar onDiscard={confirmDiscard} onKeep={cancelDiscard} />
        )}

        <form onSubmit={onFormSubmit} className="space-y-6">
          <InvestmentBasicFields formData={formData} onFieldChange={handleInputChange} config={config} errors={errors} />

          {formData.type ? (
            <>
              <OwnershipTypeSelector
                ownerType={formData.owner_type} entityId={formData.entity_id}
                entities={entities} relatedAssets={relatedAssets}
                onOwnerTypeChange={(type) => setFormData({ ...formData, owner_type: type, entity_id: '' })}
                onEntityChange={(id) => handleInputChange('entity_id', id)}
              />
              <InvestmentFormMiddle formData={formData} onFieldChange={handleInputChange} config={config} setConfig={setConfig} errors={errors} />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <TypeSpecificFields formData={formData} onFieldChange={handleInputChange} config={config} setConfig={setConfig} errors={errors} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="purchase_date" className="portfolio-input-label">Purchase Date</Label>
                <Input id="purchase_date" data-testid="purchase-date-input" type="date" value={formData.purchase_date}
                  onChange={(e) => handleInputChange('purchase_date', e.target.value)}
                  className={`portfolio-input ${errors.purchase_date ? 'border-red-400' : ''}`} />
                <FieldError error={errors.purchase_date} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="notes" className="portfolio-input-label">Notes</Label>
                <Textarea id="notes" data-testid="notes-input" placeholder="Any additional notes about this investment..."
                  value={formData.notes} onChange={(e) => handleInputChange('notes', e.target.value)}
                  className="portfolio-input min-h-[80px] resize-none" rows={3} />
              </div>
            </>
          ) : (
            <div className="text-center py-8 text-slate-500 border border-dashed border-slate-300 rounded-lg">
              <p className="text-lg">Please select an investment type above to continue</p>
            </div>
          )}

          {/* Dividends / Distributions — only when editing an existing eligible holding */}
          {isEditing && investment && investment.investment_id && (
            <>
              <IncomeSummaryCard investment={investment} />
              <DividendsSection investment={investment} />
              <SalesSection investment={investment} />
            </>
          )}

          <div className="flex justify-end space-x-4 pt-4 border-t border-slate-200">
            <Button type="button" variant="outline" data-testid="cancel-investment-btn" onClick={requestClose} className="portfolio-button-secondary">Cancel</Button>
            <Button type="submit" data-testid="save-investment-btn" disabled={isLoading || !formData.type} className="portfolio-button-primary">{getSubmitLabel()}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddInvestmentModal;
